import { useEffect, useRef, useState } from 'react';

interface Options<T> {
  intervalMs: number;
  fetcher: () => Promise<T>;
  onError?: (e: unknown) => void;
}

export interface PollingResult<T> {
  data: T | null;
  error: unknown;
  refresh: () => void;
}

export function usePollingState<T>(opts: Options<T>): PollingResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const fetcherRef = useRef(opts.fetcher);
  const onErrorRef = useRef(opts.onError);
  const aliveRef = useRef(true);
  const reqIdRef = useRef(0);
  fetcherRef.current = opts.fetcher;
  onErrorRef.current = opts.onError;

  const run = () => {
    const myId = ++reqIdRef.current;
    fetcherRef.current()
      .then((d) => {
        if (!aliveRef.current || myId !== reqIdRef.current) return;
        setData(d);
        setError(null);
      })
      .catch((e) => {
        if (!aliveRef.current || myId !== reqIdRef.current) return;
        setError(e);
        onErrorRef.current?.(e);
      });
  };

  useEffect(() => {
    aliveRef.current = true;
    run();
    let timer: number | null = null;
    const start = () => {
      if (timer !== null) return;
      timer = window.setInterval(run, opts.intervalMs);
    };
    const stop = () => {
      if (timer !== null) { clearInterval(timer); timer = null; }
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') { run(); start(); }
      else { stop(); }
    };
    document.addEventListener('visibilitychange', onVis);
    start();
    return () => {
      aliveRef.current = false;
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.intervalMs]);

  return { data, error, refresh: run };
}
